if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      }
      this.mql = window.matchMedia('(min-width: 750px)');
      this.productInfo = document.getElementById(`ProductInfo-${this.dataset.section}`);
      this.prependMedia = this.dataset.disablePrepend != 'true';
      if (this.productInfo && Shopify.postLinksRetry) this.productInfo.initShareLinks();
      this.filteringOption = this.dataset.filteringOption;
      this.infiniteScroll = this.dataset.infiniteScroll === 'true';
      if (this.infiniteScroll) window.requestAnimationFrame(() => this.initInfiniteScroll());
      if (!this.elements.thumbnails) return;

      this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
      this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
        mediaToSwitch.querySelector('button').addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
      });
      if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
    }

    initInfiniteScroll() {
      const viewer = this.elements.viewer;
      if (!viewer || !viewer.slider || !viewer.prevButton || !viewer.nextButton) return;

      this.infiniteScrollState = {
        normalizing: false,
        normalizeTimer: null,
        resizeTimer: null,
        originalUpdate: typeof viewer.update === 'function' ? viewer.update.bind(viewer) : null
      };

      viewer.enableSliderLooping = true;
      viewer.update = this.updateInfiniteScrollSlider.bind(this);
      viewer.prevButton.addEventListener('click', this.onInfiniteScrollButtonClick.bind(this), { capture: true });
      viewer.nextButton.addEventListener('click', this.onInfiniteScrollButtonClick.bind(this), { capture: true });
      viewer.slider.addEventListener('scroll', this.scheduleInfiniteScrollNormalize.bind(this), { passive: true });
      window.addEventListener('resize', this.scheduleInfiniteScrollRefresh.bind(this), { passive: true });
      this.refreshInfiniteScrollClones();
      this.refreshInfiniteScrollControls();
    }

    getVisibleSliderItems() {
      const viewer = this.elements.viewer;
      if (!viewer) return [];

      if (viewer.sliderItemsToShow) {
        return Array.from(viewer.sliderItemsToShow).filter((item) => item.clientWidth > 0 && item.dataset.mediaGalleryClone == null);
      }

      return Array.from(viewer.sliderItems || []).filter((item) => item.clientWidth > 0 && item.dataset.mediaGalleryClone == null);
    }

    refreshInfiniteScrollClones(targetMediaId = null) {
      const viewer = this.elements.viewer;
      if (!this.infiniteScroll || !viewer || !viewer.slider) return;

      const currentMediaId = targetMediaId || this.getCurrentInfiniteScrollItem()?.dataset.mediaId;
      viewer.slider.querySelectorAll('[data-media-gallery-clone]').forEach((clone) => clone.remove());
      if (typeof viewer.resetPages === 'function') viewer.resetPages();

      const items = this.getVisibleSliderItems();
      if (items.length < 2) {
        this.refreshInfiniteScrollControls();
        return;
      }

      const beforeClone = this.createInfiniteScrollClone(items[items.length - 1], 'before');
      const afterClone = this.createInfiniteScrollClone(items[0], 'after');
      viewer.slider.prepend(beforeClone);
      viewer.slider.append(afterClone);

      if (typeof viewer.resetPages === 'function') viewer.resetPages();
      this.jumpToInfiniteScrollItem(
        items.find((item) => item.dataset.mediaId === currentMediaId) || items[0]
      );
      this.updateInfiniteScrollSlider();
    }

    createInfiniteScrollClone(item, position) {
      const clone = item.cloneNode(true);
      clone.removeAttribute('id');
      clone.removeAttribute('data-media-id');
      clone.dataset.mediaGalleryClone = position;
      clone.setAttribute('aria-hidden', 'true');
      clone.classList.remove('is-active');
      clone.classList.add('product__media-gallery-loop-clone');
      clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
      clone.querySelectorAll('a, button, input, select, textarea, video, model-viewer, iframe').forEach((element) => {
        element.setAttribute('tabindex', '-1');
      });
      return clone;
    }

    scheduleInfiniteScrollRefresh() {
      if (!this.infiniteScrollState) return;

      window.clearTimeout(this.infiniteScrollState.resizeTimer);
      this.infiniteScrollState.resizeTimer = window.setTimeout(() => {
        this.refreshInfiniteScrollClones();
      }, 150);
    }

    scheduleInfiniteScrollNormalize() {
      const viewer = this.elements.viewer;
      if (!this.infiniteScroll || !this.infiniteScrollState || !viewer || !viewer.slider) return;

      if (!this.infiniteScrollState.normalizing) this.updateInfiniteScrollSlider();
      window.clearTimeout(this.infiniteScrollState.normalizeTimer);
      this.infiniteScrollState.normalizeTimer = window.setTimeout(() => {
        this.normalizeInfiniteScrollPosition();
      }, 120);
    }

    normalizeInfiniteScrollPosition() {
      const viewer = this.elements.viewer;
      if (!this.infiniteScroll || !this.infiniteScrollState || !viewer || !viewer.slider) return;

      const items = this.getVisibleSliderItems();
      if (items.length < 2) return;

      const beforeClone = viewer.slider.querySelector('[data-media-gallery-clone="before"]');
      const afterClone = viewer.slider.querySelector('[data-media-gallery-clone="after"]');
      if (!beforeClone || !afterClone) return;

      const position = this.getInfiniteScrollPosition();
      const beforePosition = this.getInfiniteScrollItemPosition(beforeClone);
      const afterPosition = this.getInfiniteScrollItemPosition(afterClone);
      const tolerance = Math.max(2, this.getInfiniteScrollItemOffset() / 4);

      if (position <= beforePosition + tolerance) {
        this.jumpToInfiniteScrollItem(items[items.length - 1]);
      } else if (position >= afterPosition - tolerance) {
        this.jumpToInfiniteScrollItem(items[0]);
      }
    }

    getInfiniteScrollPosition() {
      const viewer = this.elements.viewer;
      if (!viewer || !viewer.slider) return 0;

      return viewer.vertical ? viewer.slider.scrollTop : viewer.slider.scrollLeft * (viewer.scrollMultiplier || 1);
    }

    getInfiniteScrollItemPosition(item) {
      const viewer = this.elements.viewer;
      if (!viewer || !item) return 0;

      return viewer.vertical ? item.offsetTop : item.offsetLeft * (viewer.scrollMultiplier || 1);
    }

    getInfiniteScrollStartOffset() {
      const viewer = this.elements.viewer;
      const firstItem = this.getVisibleSliderItems()[0];
      if (!viewer || !viewer.slider || !firstItem) return 0;

      return this.getInfiniteScrollItemPosition(firstItem);
    }

    getInfiniteScrollItemOffset() {
      const viewer = this.elements.viewer;
      const items = this.getVisibleSliderItems();
      if (!viewer || items.length < 2) return viewer?.sliderItemOffset || 0;

      return Math.abs(this.getInfiniteScrollItemPosition(items[1]) - this.getInfiniteScrollItemPosition(items[0])) || viewer.sliderItemOffset || 0;
    }

    getCurrentInfiniteScrollIndex() {
      const items = this.getVisibleSliderItems();
      if (items.length < 2) return 0;

      const offset = this.getInfiniteScrollItemOffset();
      if (!offset) return 0;

      const rawIndex = Math.round((this.getInfiniteScrollPosition() - this.getInfiniteScrollStartOffset()) / offset);
      return Math.max(0, Math.min(rawIndex, items.length - 1));
    }

    getCurrentInfiniteScrollItem() {
      const items = this.getVisibleSliderItems();
      return items[this.getCurrentInfiniteScrollIndex()] || items[0] || null;
    }

    jumpToInfiniteScrollItem(item) {
      const viewer = this.elements.viewer;
      if (!viewer || !viewer.slider || !item || !this.infiniteScrollState) return;

      const slider = viewer.slider;
      const previousScrollBehavior = slider.style.scrollBehavior;
      const previousScrollSnapType = slider.style.scrollSnapType;

      this.infiniteScrollState.normalizing = true;
      slider.style.scrollBehavior = 'auto';
      slider.style.scrollSnapType = 'none';

      if (viewer.vertical) {
        slider.scrollTop = item.offsetTop;
      } else {
        slider.scrollLeft = item.offsetLeft;
      }

      window.requestAnimationFrame(() => {
        slider.style.scrollBehavior = previousScrollBehavior;
        slider.style.scrollSnapType = previousScrollSnapType;
        this.infiniteScrollState.normalizing = false;
        this.updateInfiniteScrollSlider();
        this.refreshInfiniteScrollControls();
      });
    }

    scrollToInfiniteScrollItem(item, behavior = 'smooth') {
      const viewer = this.elements.viewer;
      if (!viewer || !viewer.slider || !item) return;

      const scrollBehavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : behavior;
      if (scrollBehavior === 'auto') {
        this.jumpToInfiniteScrollItem(item);
        return;
      }

      this.infiniteScrollState.normalizing = scrollBehavior === 'auto';
      if (viewer.vertical) {
        viewer.slider.scrollTo({ top: item.offsetTop, behavior: scrollBehavior });
      } else {
        viewer.slider.scrollTo({ left: item.offsetLeft, behavior: scrollBehavior });
      }
      window.setTimeout(() => {
        this.infiniteScrollState.normalizing = false;
        this.updateInfiniteScrollSlider();
        this.refreshInfiniteScrollControls();
      }, scrollBehavior === 'auto' ? 0 : 500);
    }

    refreshInfiniteScrollControls() {
      const viewer = this.elements.viewer;
      if (!this.infiniteScroll || !viewer || !viewer.prevButton || !viewer.nextButton) return;

      const hasMultipleItems = this.getVisibleSliderItems().length > 1;
      if (hasMultipleItems) {
        viewer.prevButton.removeAttribute('disabled');
        viewer.nextButton.removeAttribute('disabled');
      } else {
        viewer.prevButton.setAttribute('disabled', 'disabled');
        viewer.nextButton.setAttribute('disabled', 'disabled');
      }
    }

    onInfiniteScrollButtonClick(event) {
      const viewer = this.elements.viewer;
      if (!this.infiniteScroll || !viewer || !viewer.slider) return;
      if (typeof viewer.initPages === 'function') viewer.initPages();

      const items = this.getVisibleSliderItems();
      if (items.length < 2) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      const isNext = event.currentTarget.name === 'next';
      const targetIndex = this.getCurrentInfiniteScrollIndex() + (isNext ? 1 : -1);

      if (targetIndex < 0) {
        this.scrollViewerToClone('before');
      } else if (targetIndex >= items.length) {
        this.scrollViewerToClone('after');
      } else {
        this.scrollToInfiniteScrollItem(items[targetIndex]);
      }
    }

    scrollViewerToClone(position) {
      const viewer = this.elements.viewer;
      if (!viewer || !viewer.slider) return;

      const clone = viewer.slider.querySelector(`[data-media-gallery-clone="${position}"]`);
      if (!clone) return;
      this.scrollToInfiniteScrollItem(clone);
    }

    updateInfiniteScrollSlider() {
      const viewer = this.elements.viewer;
      if (!viewer || !viewer.slider) return;

      const items = this.getVisibleSliderItems();
      if (items.length < 2) {
        if (this.infiniteScrollState?.originalUpdate) this.infiniteScrollState.originalUpdate();
        return;
      }

      const previousPage = viewer.currentPage;
      const currentIndex = this.getCurrentInfiniteScrollIndex();
      viewer.currentPage = currentIndex + 1;
      viewer.totalPages = items.length;
      if (viewer.currentPageElement) viewer.currentPageElement.textContent = viewer.currentPage;
      if (viewer.pageTotalElement) viewer.pageTotalElement.textContent = viewer.totalPages;

      if (viewer.currentPage !== previousPage) {
        viewer.dispatchEvent(new CustomEvent('slideChanged', {
          detail: {
            currentPage: viewer.currentPage,
            currentElement: items[currentIndex]
          }
        }));
      }

      if (viewer.hasDots && viewer.sliderControlLinksArray) {
        let visibleDotIndex = 0;
        viewer.sliderControlLinksArray.forEach((link) => {
          link.classList.remove('slider-counter__link--active');
          link.removeAttribute('aria-current');
          if (!link.classList.contains('hidden')) {
            if (visibleDotIndex === currentIndex) {
              link.classList.add('slider-counter__link--active');
              link.setAttribute('aria-current', 'true');
            }
            visibleDotIndex++;
          }
        });
      }

      const activeItem = items[currentIndex];
      if (viewer.pauseVideos && activeItem) {
        viewer.internalVideos.forEach((video) => {
          if (activeItem.id != video.closest('[id^="Slide-"]')?.id) {
            if (video.dataset.autoplay === 'true') {
              video.querySelector('video').muted = true;
              video.classList.add('internal-video--muted');
            } else {
              video.querySelector('video').pause();
              video.classList.remove('internal-video--playing');
            }
          }
        });
      }

      this.refreshInfiniteScrollControls();
    }

    onSlideChanged(event) {
      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ event.detail.currentElement.dataset.mediaId }"]`);
      this.setActiveThumbnail(thumbnail);
    }

    setActiveMedia(mediaId, prepend, filtering = false, currentVariant = null) {
      if (filtering && currentVariant) {
        const allSlides = this.querySelectorAll('.product__media-item, .thumbnail-list__item, .slider-counter__link');
        allSlides.forEach(slide => {
          if (slide.dataset.alt === currentVariant[this.filteringOption] || slide.dataset.alt === 'always_display') {
            slide.classList.remove('hidden');
          } else {
            slide.classList.add('hidden');
          }
        })
      }

      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');

      if (prepend && this.prependMedia) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
          activeThumbnail.parentElement.prepend(activeThumbnail);
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        if (this.infiniteScroll) this.refreshInfiniteScrollClones(mediaId);
      }

      this.preventStickyHeader();
      window.setTimeout(() => {
        if (this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }
        if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
          activeMedia.scrollIntoView({behavior: 'smooth'});
        }
      });
      this.playActiveMedia(activeMedia);

      if (filtering && currentVariant) {
        if (this.elements.viewer && this.elements.viewer.initPages) this.elements.viewer.initPages();
        if (this.infiniteScroll) this.refreshInfiniteScrollClones(mediaId);
        if (this.elements.viewer && this.elements.viewer.update) this.elements.viewer.update();
        this.refreshInfiniteScrollControls();
      }

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      this.setActiveThumbnail(activeThumbnail);
      this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
      thumbnail.querySelector('button').setAttribute('aria-current', true);
      if (this.elements.thumbnails.isSlideVisible(thumbnail, false, 10, true)) return;

      if (this.elements.thumbnails.vertical) {
        console.log('setActiveThumbnail')
        this.elements.thumbnails.slider.scrollTo({ top: thumbnail.offsetTop });
      } else {
        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }
  });
}
